import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";

export default function AdminLeadDetail() {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => { (async () => {
    try { setData((await api.get(`/admin/leads/${lead_id}`)).data); }
    catch { toast.error("Could not load lead"); }
  })(); }, [lead_id]);

  if (!data) return <div className="text-ink-soft text-sm">Loading…</div>;
  const { lead, related_tool_runs } = data;

  const convert = async () => {
    setConverting(true);
    try {
      const r = await api.post(`/admin/leads/${lead_id}/convert-to-client`);
      toast.success(r.data.created ? "Client created" : "Linked to existing client");
      navigate(`/admin/clients/${r.data.user_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Convert failed");
      setConverting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-lead-detail">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-stone hover:text-ivy">
        <ChevronLeft className="h-3 w-3" /> All leads
      </Link>
      <header className="border-b border-stone/30 pb-5">
        <h1 className="font-serif text-3xl text-ink tracking-tight">{lead.name || lead.email}</h1>
        <div className="flex flex-wrap gap-4 mt-2 text-xs">
          <span className="font-mono text-ink-soft">{lead.email}</span>
          <span className="text-stone">{lead.source}</span>
          <span className="text-brass uppercase tracking-[0.18em] text-[10px]">{lead.status}</span>
        </div>
      </header>

      <div className="grid lg:grid-cols-12 gap-7">
        <div className="lg:col-span-7 border border-stone/40 bg-bone p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Lead details</div>
          <dl className="mt-4 space-y-2 text-sm">
            {Object.entries(lead).map(([k, v]) => (
              <div key={k} className="grid grid-cols-3 gap-3 border-b border-stone/20 pb-2">
                <dt className="text-[10px] uppercase tracking-[0.18em] text-stone">{k}</dt>
                <dd className="col-span-2 text-ink font-mono text-xs break-all">{typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-5 space-y-5">
          <div className="border border-stone/40 bg-bone p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Related tool runs</div>
            {related_tool_runs.length === 0 ? <p className="text-sm text-ink-soft mt-3">None.</p> : (
              <ul className="mt-3 text-xs space-y-2">{related_tool_runs.map((r) => (
                <li key={r.run_id} className="border-b border-stone/20 pb-2">
                  <div className="text-ink">{r.tool}</div>
                  <div className="text-stone font-mono text-[10px]">{(r.created_at || "").slice(0, 16)}</div>
                </li>
              ))}</ul>
            )}
          </div>

          <button onClick={convert} disabled={converting} className="w-full bg-ivy text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 disabled:opacity-60" data-testid="convert-to-client-btn">
            {converting ? "Converting…" : "Convert to client →"}
          </button>
        </div>
      </div>
    </div>
  );
}
